<template>
  <div>
    <div class="heading" :class="{ 'spinner': isLoading }">
      <h3>Patients to be Scheduled</h3>
    </div>
    <div class="patient-to-be-scheduled-div" v-if="!isLoading">
      <DataTable :value="sortedToBeScheduledPatientDataWithSerialNumbers" editMode="cell"
        @cell-edit-complete="onToBeScheduledTableCellEditComplete" paginator :alwaysShowPaginator=false :rows=5
        tableStyle="min-width: 10rem" tableClass="editable-cells-table" @row-click="onSelectPatientToBeScheduled">
        <template v-if="!sortedToBeScheduledPatientDataWithSerialNumbers?.length && !isLoading" #empty>
          <p class="no-records-class"> No Patients to be Scheduled</p>
        </template>
        <Column field="serialNumber" header="" sortable>
        </Column>
        <Column field="given" header="PATIENT NAME" sortable>
          <template #body="{ data }">
            {{ data.given }} {{ data.family }}
          </template>
        </Column>
        <Column field="name" header="REQUESTING PHYSICIAN" sortable></Column>
        <Column field="gender" header="AGE/GENDER">
          <template #body="{ data }">
            {{ calculateAge(data.birthdate) }}/{{ data.gender }}
          </template>
        </Column>
        <Column field="mrn" header="MRN"></Column>
        <Column field="diagnosis" header="DIAGNOSIS">
        </Column>
        <Column field="tbName" header="TUMOR BOARD">
          <template #body="{ data, field }">
            <select v-model="data[field]" id="select1" class="text-input" @change="handleTbSelectChange(data[field])">
              <option v-for="item in tumorBoardsDropdown" :key="item.tb_id" :value="item.tb_name">{{ item.tb_name }}
              </option>
            </select>
          </template>
        </Column>
        <Column field="notes" header="NOTE/REQUEST">
        </Column>
        <Column field="priority" header="PRIORITY">
          <template #body="{ field, data }">
            <div :class="getPriorityClass(data[field])">
              {{ data[field] }}
            </div>
          </template>
        </Column>
        <Column field="date" header="DATE">
          <template #body="" v-if="showIconOnLoad">
            <span><i class="material-symbols-outlined icon-button-to-primary" title="Calendar">edit_calendar</i></span>
          </template>
          <template #editor="{ data, field }">
            <div class="calendar-container">
              <span><i class="material-symbols-outlined icon-button-to-primary"
                  title="Calendar">edit_calendar</i></span>
              <Calendar v-model="data[field]" showIcon:minDate="today" :manualInput="false" dateFormat="dd-M-yy"
                @onSelect="handleCalenderSelect" @date-select="handleDateSelect(data[field], data.mrn)"
                :minDate="minDate">
                <template #date="slotProps">
                  <strong v-if="isHighlightedDatePatientToBeScheduled(slotProps.date)" style="color: var(--Primary)">
                    {{ slotProps.date.day }}
                  </strong>
                  <template v-else>
                    {{ slotProps.date.day }}
                  </template>
                </template>
              </Calendar>
            </div>
          </template>
        </Column>
        <Column field="" header="">
          <template #body="slotProps">
            <span @click="handleAddToAgenda(slotProps.data)"
              class="material-symbols-outlined icon-button-primary-filled" title="Add to schedule"
              :class="{ 'disabled-button': !slotProps.data.date }">add</span>
          </template>
        </Column>
        <Column field="" header="">
          <template #body="slotProps">
            <span @click="openDeleteModal(slotProps.data)" class="material-symbols-outlined icon-button-to-primary"
              title="Delete">delete</span>
          </template>
        </Column>
      </DataTable>
    </div>
    <Transition name="fade">
      <div class="modal" v-show="showDelete">
        <DeleteModal cardName="Remove from Scheduling Tool" :showDelete="true"
          :actionAndEntity="`remove the request to schedule for ${itemToDelete.given} ${itemToDelete.family}`"
          @close-delete="closeDeleteModal()" @delete="handleDelete">
        </DeleteModal>
      </div>
    </Transition>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getPatientDetailsByMRN } from '@/ux3/cards/TumorBoardSideBar/getPatientDetailsByMRN'
import { calculateAge, formatDate, formatDateForHighlight, formatDateToTBPayload, mergeByMrn, mergeByPhysicianId, showToast } from '@/utils/utils.js'
import DataTable from 'primevue/datatable'
import Calendar from 'primevue/calendar'
import Column from 'primevue/column'
import DeleteModal from '@/ux3/components/Common/DeleteModal.vue'
import { addToAgenda } from '@/ux3/cards/TumorBoardSessions/addToAgenda'
import { removeFromAgenda } from '@/ux3/cards/TumorBoardSessions/removeFromAgenda'
import { getScheduleAPI } from '@/ux3/cards/TumorBoardSideBar/getScheduleAPI'
import { getHighlightedDates } from '../TumorBoardSideBar/getHighlightedDates'
import { getTumorBoards } from '@/ux3/cards/TumorBoardSessions/getTumorBoards'

const store = useStore()
const schedulePatientsFromStore = computed(() => store.state.session.schedulePatients)
const toBeScheduled = ref([])
const patientDetailsByMRN = ref([])
const requestingPhysician = computed(() => store.state.session.requestingPhysician)
const sortedToBeScheduledPatientDataWithSerialNumbers = ref([])
const patientToBeScheduledHighlightedDates = ref([])
const tumorBoardsDropdown = ref([])
const changedTbId = ref('')
const showIconOnLoad = ref(false)
const isLoading = ref(true)
const minDate = ref(new Date())
const showDelete = ref(false)
const itemToDelete = ref({})

const emit = defineEmits(['form-change', 'reset-confirmation'])

const handleTbSelectChange = (selectedValue) => {
  emit('form-change')
  changedTbId.value = tumorBoardsDropdown.value.find(tb => tb.tb_name === selectedValue)?.tb_id || ''
}

onMounted(async () => {
  const tumorBoardsData = await getTumorBoards()
  tumorBoardsDropdown.value = tumorBoardsData?.tb_types?.map(item => ({ tb_name: item.tb_name, tb_id: item.tb_id })) || []
  showIconOnLoad.value = true
})

const handleCalenderSelect = () => showIconOnLoad.value = false

const handleDateSelect = (selectedDate, mrn) => {
  const formattedDate = formatDate(selectedDate)
  sortedToBeScheduledPatientDataWithSerialNumbers.value.filter((row) => row.mrn === mrn)[0].date = formattedDate
}

watch(schedulePatientsFromStore, async (newSchedulePatients) => {
  if (!newSchedulePatients) { return }
  minDate.value = new Date()
  toBeScheduled.value = newSchedulePatients.not_scheduled
  const mrnListToBeScheduled = newSchedulePatients.not_scheduled?.map((item) => item.mrn) || []
  if (mrnListToBeScheduled.length) {
    const patientDetailsByMRNFromResponse = await getPatientDetailsByMRN(mrnListToBeScheduled)
    patientDetailsByMRN.value = patientDetailsByMRNFromResponse
  }
  toBeScheduled.value = tableDataWithMRNPhysicianDetails(toBeScheduled.value)
  sortedToBeScheduledPatientDataWithSerialNumbers.value = toBeScheduled.value?.map((item, index) => {
    return { ...item, tbName: item.calendar.tumor_board.tb_name, serialNumber: index + 1 } || []
  })
  isLoading.value = false
}, { immediate: true })

const onToBeScheduledTableCellEditComplete = (event) => {
  emit('form-change')
  const { data, newValue, field, value } = event
  if (value === newValue) { return }
  switch (field) {
    case 'diagnosis':
    case 'priority':
      data[field] = typeof newValue === 'string' ? newValue : newValue[field]
      break
    case 'date':
      data[field] = typeof newValue === 'string' ? newValue : formatDate(newValue)
      break
    default:
      data[field] = newValue
      break
  }
}

const onSelectPatientToBeScheduled = async (event) => {
  const rowData = event.data
  const patientToBeScheduledHighlightedDatesData = await getHighlightedDates(changedTbId.value || rowData.calendar.tb_id)
  patientToBeScheduledHighlightedDates.value = patientToBeScheduledHighlightedDatesData
}

const handleAddToAgenda = async (rowData) => {
  if (!rowData.date) {
    showToast('warning', 'Please select a date')
    return
  }
  const rowDataObject = {
    date: formatDateToTBPayload(rowData.date),
    notes: rowData.notes,
    priority: rowData.priority,
    schedule_id: rowData.schedule_id,
    tb_id: changedTbId.value || rowData.calendar.tb_id,
  }

  const response = await addToAgenda([rowDataObject])
  const keyToCheck = 'schedule_ids'
  if (response?.[keyToCheck] !== undefined) {
    showToast('success', 'Successfully added patient to schedule')
    const schedulePatientFromResponse = await getScheduleAPI()
    store.dispatch('setScheduledAndToBeScheduledPatients', { schedulePatients: schedulePatientFromResponse, publish: true })
    emit('reset-confirmation')
  }
  else if (response?.status === 404) {
    showToast('error', 'This Tumorboard does not meet on the date selected. Please select a different date.')
  }
  else {
    showToast('error', 'Failed to add patient to the agenda. Please try again.')
  }
}

const handleRemoveFromScheduling = async (data) => {
  showDelete.value = false
  if (!data) { return }

  //DELETE
  const status = await removeFromAgenda(data.schedule_id)

  if (status === 204) {
    showToast('success', 'Successfully removed from the scheduling tool.')
    const schedulePatientFromResponse = await getScheduleAPI()
    store.dispatch('setScheduledAndToBeScheduledPatients', { schedulePatients: schedulePatientFromResponse, publish: true })
    emit('reset-confirmation')
  } else {
    showToast('error', 'Failed to remove from the scheduling tool. Please try again.')
  }
}

const closeDeleteModal = () => {
  showDelete.value = false
  itemToDelete.value = {}
}

const openDeleteModal = (data) => {
  showDelete.value = true
  itemToDelete.value = data
}

const handleDelete = async () => {
  await handleRemoveFromScheduling(itemToDelete.value)
}

const tableDataWithMRNPhysicianDetails = (array) => {
  let tableData = array
  if (array?.length && patientDetailsByMRN.value?.length) {
    tableData = mergeByMrn(array, patientDetailsByMRN.value)
  }
  if (array?.length && requestingPhysician.value?.length) {
    tableData = mergeByPhysicianId(tableData, requestingPhysician.value)
  }
  return tableData
}

const isHighlightedDatePatientToBeScheduled = (dateObj) => {
  const formattedDate = formatDateForHighlight(dateObj)
  return patientToBeScheduledHighlightedDates.value.dates?.includes(formattedDate)
}

const getPriorityClass = (priority) => priority ? `${priority.toLowerCase()}-priority` : ''

</script>

<style lang="scss">
.calendar-container {
  display: flex;
  min-width: 155px;

  .p-calendar input {
    display: inline-block;
    width: 100%;
    min-width: 56px;
  }
}

.text-input {
  cursor: pointer;

}

.patient-to-be-scheduled-div .p-column-header-content {
  font-size: 12px;
  font-weight: 500;
  color: var(--Grey600);
  font-family: 'Roboto';
}

.patient-to-be-scheduled-div .p-datatable .p-datatable-tbody>tr>td {
  font-size: 14px;
  font-weight: 400;
  color: var(--Black);
  font-family: 'Roboto';
  position: relative;
}

.patient-to-be-scheduled-div .p-datatable .p-datatable-tbody>tr>td>select {
  margin-bottom: 0;
}

.patient-to-be-scheduled-div .p-datatable .p-sortable-column:first-child .p-sortable-column-icon {
  margin-left: 0;
}

.p-dropdown {
  max-width: 235px;
}

.disabled-button,
.disabled-button:hover {
  background-color: var(--Grey300);
  cursor: not-allowed;
}

.no-records-class {
  color: var(--Black);
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
}

.p-datatable {
  border: 1px solid var(--Grey100);
  margin: 0 28px;
}

.heading {
  margin-left: 12px;
}

.low-priority,
.standard-priority,
.urgent-priority {
  font-weight: bold;
}

.low-priority {
  color: var(--Grey500);
}

.standard-priority {
  color: var(--WarningYellowDark);
}

.urgent-priority {
  color: var(--ErrorRedDark);
}

.modal {
  position: absolute;
  top: 65px;
  left: calc(50% - 23vw);
}
</style>
